import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  useDeleteAdminUser,
  useDeleteAdminUserIntegration,
  useAdminUser,
  useAdminUserIntegrations,
  useAdminUserJobs,
  useAdminUserRuns,
} from '../api/admin';
import { RelativeTime } from '../components/RelativeTime';
import {
  AdminError,
  AdminSection,
  AdminShell,
  DetailList,
  IntegrationsTable,
  AdminJobsTable,
  AdminRunsTable,
  AdminSkeletonRows,
} from './adminShared';
import { errorMessage } from '../lib/errorMessage';

export function AdminUserPage() {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const user = useAdminUser(userId);
  const integrations = useAdminUserIntegrations(userId);
  const jobs = useAdminUserJobs(userId);
  const runs = useAdminUserRuns(userId);
  const deleteUser = useDeleteAdminUser();
  const deleteIntegration = useDeleteAdminUserIntegration(userId);
  const title = user.data?.email ?? 'User';
  // Every job/run on this page belongs to this one user, so a single-entry
  // lookup is enough for the jobs/runs tables to render the email.
  const userEmails = useMemo(
    () => (user.data ? new Map([[user.data.id, user.data.email]]) : undefined),
    [user.data],
  );

  const onDeleteUser = () => {
    if (!user.data || !window.confirm(`Delete user ${user.data.email}? This cannot be undone.`)) {
      return;
    }
    deleteUser.mutate(user.data.id, {
      onSuccess: () => void navigate('/admin'),
    });
  };

  return (
    <AdminShell title={title} subtitle={userId}>
      <AdminSection title="Profile">
        {user.isPending && <AdminSkeletonRows rows={3} />}
        {user.error && (
          <AdminError subject="user" error={user.error} onRetry={() => void user.refetch()} />
        )}
        {user.data && (
          <DetailList
            rows={[
              ['Email', user.data.email],
              ['User ID', <span className="font-mono text-xs">{user.data.id}</span>],
              ['Created', <RelativeTime iso={user.data.createdAt} />],
              ['Updated', <RelativeTime iso={user.data.updatedAt} />],
            ]}
          />
        )}
        {user.data && (
          <div className="border-t border-slate-100 p-4">
            <button
              type="button"
              onClick={onDeleteUser}
              disabled={deleteUser.isPending}
              className="rounded border border-red-300 bg-white px-3 py-1.5 text-sm font-semibold text-red-700 hover:border-red-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {deleteUser.isPending ? 'Deleting user...' : 'Delete user'}
            </button>
            {deleteUser.error && (
              <p className="mt-2 text-sm text-red-800">
                Couldn&apos;t delete user: {errorMessage(deleteUser.error)}
              </p>
            )}
          </div>
        )}
      </AdminSection>

      <AdminSection title="Integrations">
        {integrations.isPending && <AdminSkeletonRows />}
        {integrations.error && (
          <AdminError
            subject="integrations"
            error={integrations.error}
            onRetry={() => void integrations.refetch()}
          />
        )}
        {integrations.data && (
          <IntegrationsTable
            integrations={integrations.data}
            deletingType={deleteIntegration.variables}
            onDelete={(integration) => {
              const account = integration.externalAccountLogin ?? integration.externalAccountId;
              if (
                window.confirm(`Delete ${integration.integrationType} integration for ${account}?`)
              ) {
                deleteIntegration.mutate(integration.integrationType);
              }
            }}
          />
        )}
        {deleteIntegration.error && (
          <p className="p-4 text-sm text-red-800">
            Couldn&apos;t delete integration: {errorMessage(deleteIntegration.error)}
          </p>
        )}
      </AdminSection>

      <AdminSection title="Jobs">
        {jobs.isPending && <AdminSkeletonRows />}
        {jobs.error && (
          <AdminError subject="jobs" error={jobs.error} onRetry={() => void jobs.refetch()} />
        )}
        {jobs.data && <AdminJobsTable jobs={jobs.data} userEmails={userEmails} />}
      </AdminSection>

      <AdminSection title="Runs">
        {runs.isPending && <AdminSkeletonRows />}
        {runs.error && (
          <AdminError subject="runs" error={runs.error} onRetry={() => void runs.refetch()} />
        )}
        {runs.data && <AdminRunsTable runs={runs.data} userEmails={userEmails} />}
      </AdminSection>
    </AdminShell>
  );
}
